import type { Ref } from "preact";
import type { MentionSuggestion } from "../../utils/member-cache";
import { IcoSpinner, IcoUser, IcoUsers } from "../icons";

export type MentionDropdownProps = {
  suggestions: MentionSuggestion[];
  selectedIndex: number;
  loading: boolean;
  onSelect: (suggestion: MentionSuggestion) => void;
  onHover: (index: number) => void;
  dropdownRef?: Ref<HTMLDivElement>;
  class?: string;
  style?: Record<string, string>;
};

export function MentionDropdown({
  suggestions,
  selectedIndex,
  loading,
  onSelect,
  onHover,
  dropdownRef,
  class: className,
  style,
}: MentionDropdownProps) {
  return (
    <div
      ref={dropdownRef}
      role="listbox"
      class={`max-h-48 overflow-y-auto bg-surface border border-border rounded-lg shadow-lg z-50 ${className ?? ""}`}
      style={style}
    >
      {loading ? (
        <div class="flex items-center justify-center py-4">
          <IcoSpinner class="size-5 text-text-muted animate-spin" />
        </div>
      ) : suggestions.length === 0 ? (
        <div class="px-3 py-2 text-sm text-text-muted">No members found</div>
      ) : (
        suggestions.map((suggestion, index) => (
          <button
            key={suggestion.memberId}
            type="button"
            role="option"
            aria-selected={index === selectedIndex}
            class={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover transition-colors ${
              index === selectedIndex ? "bg-surface-hover" : ""
            }`}
            onMouseDown={(e: MouseEvent) => {
              e.preventDefault();
              onSelect(suggestion);
            }}
            onMouseEnter={() => {
              onHover(index);
            }}
          >
            {suggestion.isEveryone ? (
              <div class="size-8 rounded-full bg-primary/10 flex items-center justify-center">
                <IcoUsers class="size-4 text-primary" />
              </div>
            ) : suggestion.profilePictureUrl ? (
              <img
                src={suggestion.profilePictureUrl}
                alt=""
                class="size-8 rounded-full object-cover"
              />
            ) : (
              <div class="size-8 rounded-full bg-surface-muted flex items-center justify-center">
                <IcoUser class="size-4 text-text-muted" />
              </div>
            )}
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-text truncate">
                {suggestion.isEveryone ? "@everyone" : suggestion.name}
              </p>
              {suggestion.isEveryone && (
                <p class="text-xs text-text-muted">
                  Notify all workspace members
                </p>
              )}
            </div>
          </button>
        ))
      )}
    </div>
  );
}
